import { Edge, Node } from 'reactflow'
import { iNoOrganograma, IDepartment, IEmployee } from '../../../../../../interfaces'

const LARGURA_COLABORADOR = 120
const ALTURA_COLABORADOR = 144
const ESPACO_COLABORADOR = 8
const ALTURA_CONECTOR = 24
const COLABORADORES_POR_LINHA = 6
const PADDING_AREA = 26
const PADDING_LISTA_COLABORADORES = 16
const ALTURA_LINHA_TITULO = 27
const MARGEM_TITULO = 8
const LARGURA_MEDIA_CARACTERE_TITULO = 10.5
const LARGURA_MINIMA_AREA = 240
const ALTURA_MINIMA_AREA = 120
const FOLGA_DE_QUEBRA = 4
const ESPACO_ENTRE_IRMAOS = 60
const ESPACO_ENTRE_ARVORES = 160
const ESPACO_ENTRE_NIVEIS = 120

const LARGURA_ALVO_DOS_COLABORADORES =
  COLABORADORES_POR_LINHA * LARGURA_COLABORADOR +
  (COLABORADORES_POR_LINHA - 1) * ESPACO_COLABORADOR

interface iMedidaColaborador {
  largura: number
  niveis: number
}

interface iNoLayout {
  departamento: IDepartment
  nivel: number
  largura: number
  altura: number
  filhos: iNoLayout[]
  x: number
  esquerdaDoBloco: number
  larguraDoBloco: number
}

export function buildReactFlowGraph(
  departments: IDepartment[],
  cores: string[],
  savedNodes?: iNoOrganograma[]
): { nodes: Node[], edges: Edge[] } {
  const raizes = departments.map(departamento => montarNoDeLayout(departamento, 0))

  let deslocamentoXDaArvore = 0
  raizes.forEach(raiz => {
    posicionarSubarvore(raiz, deslocamentoXDaArvore)
    deslocamentoXDaArvore += raiz.larguraDoBloco + ESPACO_ENTRE_ARVORES
  })

  const todosOsNos: iNoLayout[] = []
  raizes.forEach(raiz => achatarArvore(raiz, todosOsNos))

  const alturaPorNivel = obterAlturaPorNivel(todosOsNos)
  const yPorNivel = obterYPorNivel(alturaPorNivel)

  const nodes: Node[] = todosOsNos.map(no => {
    const salvo = savedNodes?.find(noSalvo => noSalvo.data?.departamentoId === no.departamento.id)
    const largura = salvo?.width ?? no.largura
    const altura = salvo?.height ?? alturaPorNivel[no.nivel]

    return {
      id: no.departamento.id,
      type: 'custom',
      data: {
        departamentoId: no.departamento.id,
        label: no.departamento.nome,
        colaboradores: no.departamento.colaboradores ?? [],
        larguraCalculada: largura,
        alturaCalculada: altura,
        cor: no.departamento.cor,
        cores: cores,
        nivel: no.nivel
      },
      position: salvo?.position ?? { x: no.x, y: yPorNivel[no.nivel] },
      width: largura,
      height: altura
    }
  })

  const edges: Edge[] = []
  todosOsNos.forEach(no => {
    no.filhos.forEach(filho => {
      edges.push({
        id: `e${no.departamento.id}-${filho.departamento.id}`,
        source: no.departamento.id,
        target: filho.departamento.id
      })
    })
  })

  return { nodes, edges }
}

function montarNoDeLayout(departamento: IDepartment, nivel: number): iNoLayout {
  const medida = medirArea(departamento)

  return {
    departamento: departamento,
    nivel: nivel,
    largura: medida.largura,
    altura: medida.altura,
    filhos: (departamento.departamentoFilhos ?? []).map(filho => montarNoDeLayout(filho, nivel + 1)),
    x: 0,
    esquerdaDoBloco: 0,
    larguraDoBloco: medida.largura
  }
}

// Cada subarvore ocupa uma faixa horizontal exclusiva e o pai fica centralizado
// sobre os filhos, que e o que garante o organograma sem linha cruzada.
function posicionarSubarvore(no: iNoLayout, esquerda: number): void {
  no.esquerdaDoBloco = esquerda

  if (no.filhos.length === 0) {
    no.x = esquerda
    no.larguraDoBloco = no.largura
    return
  }

  let cursor = esquerda
  no.filhos.forEach(filho => {
    posicionarSubarvore(filho, cursor)
    cursor += filho.larguraDoBloco + ESPACO_ENTRE_IRMAOS
  })

  const primeiroFilho = no.filhos[0]
  const ultimoFilho = no.filhos[no.filhos.length - 1]
  const centroDosFilhos = (
    primeiroFilho.x + primeiroFilho.largura / 2 +
    ultimoFilho.x + ultimoFilho.largura / 2
  ) / 2

  no.x = centroDosFilhos - no.largura / 2

  // pai mais largo que a fila de filhos: empurra os filhos em vez de estourar a faixa
  if (no.x < esquerda) {
    const deslocamento = esquerda - no.x
    no.filhos.forEach(filho => deslocarSubarvore(filho, deslocamento))
    no.x = esquerda
  }

  const fimDosFilhos = Math.max(...no.filhos.map(filho => filho.esquerdaDoBloco + filho.larguraDoBloco))
  no.larguraDoBloco = Math.max(no.x + no.largura, fimDosFilhos) - esquerda
}

function deslocarSubarvore(no: iNoLayout, deslocamento: number): void {
  no.x += deslocamento
  no.esquerdaDoBloco += deslocamento
  no.filhos.forEach(filho => deslocarSubarvore(filho, deslocamento))
}

function achatarArvore(no: iNoLayout, acumulador: iNoLayout[]): void {
  acumulador.push(no)
  no.filhos.forEach(filho => achatarArvore(filho, acumulador))
}

function obterAlturaPorNivel(nos: iNoLayout[]): number[] {
  const alturas: number[] = []
  nos.forEach(no => {
    alturas[no.nivel] = Math.max(alturas[no.nivel] ?? 0, no.altura)
  })
  return alturas
}

function obterYPorNivel(alturaPorNivel: number[]): number[] {
  const posicoes: number[] = []
  let y = 0

  for (let nivel = 0; nivel < alturaPorNivel.length; nivel++) {
    posicoes[nivel] = y
    y += (alturaPorNivel[nivel] ?? 0) + ESPACO_ENTRE_NIVEIS
  }

  return posicoes
}

function medirArea(departamento: IDepartment): { largura: number, altura: number } {
  const medidas = (departamento.colaboradores ?? []).map(colaborador => medirColaborador(colaborador))
  const linhas = distribuirEmLinhas(medidas)

  const larguraDosColaboradores = linhas.length > 0
    ? Math.max(...linhas.map(linha => medirLarguraDaLinha(linha)))
    : 0

  const largura = Math.max(
    LARGURA_MINIMA_AREA,
    medirLarguraMinimaPeloTitulo(departamento.nome),
    larguraDosColaboradores + PADDING_AREA + FOLGA_DE_QUEBRA
  )

  const alturaDosColaboradores = linhas.length > 0
    ? PADDING_LISTA_COLABORADORES +
      linhas.reduce((total, linha) => total + medirAlturaDaLinha(linha), 0) +
      ESPACO_COLABORADOR * (linhas.length - 1)
    : 0

  const altura = Math.max(
    ALTURA_MINIMA_AREA,
    PADDING_AREA + medirAlturaDoTitulo(departamento.nome, largura) + alturaDosColaboradores
  )

  return { largura: largura, altura: altura }
}

// Repete o mesmo empacotamento do flex-wrap do card para que a conta de
// largura e altura bata com o que o navegador desenha.
function distribuirEmLinhas(medidas: iMedidaColaborador[]): iMedidaColaborador[][] {
  const linhas: iMedidaColaborador[][] = []
  let linhaAtual: iMedidaColaborador[] = []
  let larguraDaLinhaAtual = 0

  medidas.forEach(medida => {
    const larguraComOItem = linhaAtual.length === 0
      ? medida.largura
      : larguraDaLinhaAtual + ESPACO_COLABORADOR + medida.largura

    if (linhaAtual.length > 0 && larguraComOItem > LARGURA_ALVO_DOS_COLABORADORES) {
      linhas.push(linhaAtual)
      linhaAtual = [medida]
      larguraDaLinhaAtual = medida.largura
      return
    }

    linhaAtual.push(medida)
    larguraDaLinhaAtual = larguraComOItem
  })

  if (linhaAtual.length > 0) linhas.push(linhaAtual)

  return linhas
}

function medirLarguraDaLinha(linha: iMedidaColaborador[]): number {
  return linha.reduce((total, medida) => total + medida.largura, 0) +
    ESPACO_COLABORADOR * (linha.length - 1)
}

function medirAlturaDaLinha(linha: iMedidaColaborador[]): number {
  const niveis = Math.max(...linha.map(medida => medida.niveis))
  return niveis * ALTURA_COLABORADOR + (niveis - 1) * ALTURA_CONECTOR
}

function medirLarguraMinimaPeloTitulo(nome: string): number {
  const larguraDoTexto = nome.length * LARGURA_MEDIA_CARACTERE_TITULO
  return Math.min(LARGURA_ALVO_DOS_COLABORADORES, larguraDoTexto / 2 + PADDING_AREA)
}

function medirAlturaDoTitulo(nome: string, larguraDaArea: number): number {
  const larguraDisponivel = Math.max(1, larguraDaArea - PADDING_AREA)
  const linhas = Math.max(1, Math.ceil((nome.length * LARGURA_MEDIA_CARACTERE_TITULO) / larguraDisponivel))
  return linhas * ALTURA_LINHA_TITULO + MARGEM_TITULO
}

function medirColaborador(colaborador: IEmployee): iMedidaColaborador {
  const subordinados = colaborador.subordinados ?? []

  if (subordinados.length === 0) {
    return { largura: LARGURA_COLABORADOR, niveis: 1 }
  }

  const medidas = subordinados.map(subordinado => medirColaborador(subordinado))
  const larguraDosSubordinados = medidas.reduce((total, medida) => total + medida.largura, 0) +
    ESPACO_COLABORADOR * (medidas.length - 1)

  return {
    largura: Math.max(LARGURA_COLABORADOR, larguraDosSubordinados),
    niveis: 1 + Math.max(...medidas.map(medida => medida.niveis))
  }
}

export interface iPosicaoTopo {
  x: number
  y: number
}

export function medirTopo(colaborador: IEmployee): { largura: number, altura: number } {
  const medida = medirColaborador(colaborador)
  return { largura: medida.largura, altura: medirAlturaDaLinha([medida]) }
}

// mesma quebra em linhas do layout automático, usada como posição inicial dos líderes
export function posicionarTopos(colaboradores: IEmployee[]): Record<string, iPosicaoTopo> {
  const linhas = distribuirEmLinhas(colaboradores.map(medirColaborador))
  const posicoes: Record<string, iPosicaoTopo> = {}
  let indice = 0
  let y = 0

  linhas.forEach(linha => {
    let x = 0
    linha.forEach(medida => {
      posicoes[colaboradores[indice].id] = { x, y }
      x += medida.largura + ESPACO_COLABORADOR
      indice++
    })
    y += medirAlturaDaLinha(linha) + ESPACO_COLABORADOR
  })

  return posicoes
}
